import PropTypes from 'prop-types';
import hasPath from "ramda/es/hasPath";
import cx from "ramda-extension/es/cx";
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import FormGroup from '../../../molecules/FormGroup';
import Label from '../../../molecules/Label';
import { resolveValidation } from '../../../molecules/Validation';
import { validationDisplayTypes } from '../../../molecules/Validation/constants';
import when from '../../../utils/when';
const COMPONENT_NAME = 'TextFieldComposer';
const isValid = (value)=>value != null && value !== '';
function textFieldComposer(WrappedComponent) {
    class TextFieldComposer extends Component {
        constructor(props){
            super(props);
            this.state = {
                isDirty: false,
                isFocused: false
            };
            this._uniqueId = crypto.randomUUID();
        }
        handleChange = (e)=>{
            const { disabled, readOnly } = this.props;
            if (!disabled && !readOnly) {
                const value = e.target.value;
                // TODO TEST
                if (!this.state.isDirty) {
                    this.setState({
                        isDirty: true,
                        hasValue: isValid(value)
                    });
                }
                this.props.onChange?.(e, value);
            }
        };
        handleFocus = (e)=>{
            if (!this.props.readOnly) {
                this.setState({
                    isFocused: true
                });
                this.props.onFocus?.(e);
            }
        };
        handleBlur = (e)=>{
            this.setState({
                isFocused: false
            });
            this.props.onBlur?.(e);
        };
        getInputNode() {
            return this.props.children && this.control.getInputNode ? this.control.getInputNode() : findDOMNode(this.control);
        }
        focus() {
            if (!this.props.readOnly) {
                const node = this.getInputNode();
                if (node) {
                    node.focus();
                }
            }
        }
        blur() {
            const node = this.getInputNode();
            if (node) {
                node.blur();
            }
        }
        render() {
            const { className, labelClassName, hideValidationText, validationClassName, labelText, labelNote, labelPosition, validationText, disabled, readOnly, value, autoComplete, placeholder, panelControls, controlClassName, floatingLabelText, floatingLabelClassName, type, id, required, validationDisplayType, tooltipComponent, inputWrapperProps, onInputWrapperClick, ...other } = this.props;
            const controlId = id || this._uniqueId;
            const validationElement = resolveValidation({
                hideValidationText,
                validationText,
                validationClassName,
                validationDisplayType,
                tooltipComponent
            });
            const labelElement = when(labelText, /*#__PURE__*/ React.createElement(Label, {
                className: labelClassName,
                note: labelNote,
                htmlFor: controlId
            }, labelText));
            const controlProps = {
                disabled,
                readOnly,
                value,
                onChange: this.handleChange,
                onFocus: this.handleFocus,
                onBlur: this.handleBlur,
                id: controlId,
                autoComplete: autoComplete === 'workaround-off' ? `new-${id}` : autoComplete,
                placeholder,
                ref: (elem)=>{
                    this.control = elem;
                    if (hasPath([
                        'inputControlRef',
                        'current'
                    ], other)) {
                        other.inputControlRef.current = elem;
                    }
                }
            };
            const inputProps = {
                htmlFor: controlId,
                controls: panelControls,
                className: controlClassName,
                type,
                hasError: !!validationText,
                disabled,
                required,
                readOnly,
                floatingLabelText,
                floatingLabelClassName,
                onInputWrapperClick: (e)=>{
                    this.focus();
                    onInputWrapperClick?.(e);
                },
                inputWrapperProps
            };
            const wrappedComponentProps = {
                controlProps,
                inputProps,
                type,
                isFocused: this.state.isFocused,
                value,
                ...other
            };
            return /*#__PURE__*/ React.createElement(FormGroup, {
                className: cx('Textfield', className),
                validation: validationElement,
                label: labelElement,
                labelPosition: labelPosition
            }, /*#__PURE__*/ React.createElement(WrappedComponent, wrappedComponentProps));
        }
    }
    TextFieldComposer.propTypes = {
        // for the sake of brevity, description of propTypes are in TextField.
        autoComplete: PropTypes.string,
        children: PropTypes.node,
        className: PropTypes.string,
        controlClassName: PropTypes.string,
        controlInputClassName: PropTypes.string,
        controlTextareaClassName: PropTypes.string,
        disabled: PropTypes.bool,
        floatingLabelClassName: PropTypes.string,
        floatingLabelText: PropTypes.node,
        hideValidationText: PropTypes.bool,
        id: PropTypes.string,
        labelClassName: PropTypes.string,
        labelNote: PropTypes.node,
        labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
        labelText: PropTypes.node,
        multiline: PropTypes.bool,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onClick: PropTypes.func,
        onFocus: PropTypes.func,
        panelControls: PropTypes.node,
        placeholder: PropTypes.string,
        readOnly: PropTypes.bool,
        required: PropTypes.bool,
        rows: PropTypes.number,
        tooltipComponent: PropTypes.any,
        type: PropTypes.string,
        validationClassName: PropTypes.string,
        validationDisplayType: PropTypes.oneOf(Object.keys(validationDisplayTypes)),
        validationText: PropTypes.node,
        value: PropTypes.any
    };
    return withDefaultComponentProps(COMPONENT_NAME, {
        disabled: false,
        type: 'text',
        autoComplete: 'workaround-off'
    }, TextFieldComposer);
}
export default ((WrappedComponent)=>textFieldComposer(WrappedComponent));
